import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Globals } from 'app/_globals';
import { NotificationService } from 'app/_services/notification.service';
import { SiteOwnerUserService } from 'app/_services/site-owner-user.service';
import { SiteTypesService } from 'app/_services/site-types.service';
import { SiteService } from 'app/_services/site.service';
import { TrainingService } from 'app/_services/training.service';
import { GooglePlaceDirective } from 'ngx-google-places-autocomplete';
import { Address } from 'ngx-google-places-autocomplete/objects/address';

@Component({
  selector: 'app-create-site',
  templateUrl: './create-site.component.html'
})
export class CreateSiteComponent implements OnInit {

  role: any;
  routeRole: any;

  createSiteForm: FormGroup;

  siteOwners: any[] = [];
  selectedOwner = { label: 'Select Site Owner', value: null };
  siteTypes: any[] = [];
  selectedSiteType = { label: 'Select Site Type', value: null };
  siteInductions: any[] = [];
  selectedSiteInduction = { label: 'Select Site Induction', value: null };

  // Map Variables
  zoom = 8;
  lat = 53.4;
  lng = -7.60;
  @ViewChild('placesRef', { static: true }) placesRef: GooglePlaceDirective; // View Child for Google places search input
  markers: Marker[] = [];

  constructor(
    private siteService: SiteService,
    private siteOwnerUserService: SiteOwnerUserService,
    private siteTypesService: SiteTypesService,
    private trainingService: TrainingService,
    private notificationService: NotificationService,
    private router: Router,
    private globals: Globals,
    private fb: FormBuilder,
  ) {
    this.role = globals.user.role;
    this.routeRole = globals.user.routeRole;

    this.createSiteForm = fb.group({
      name: new FormControl(),
      description: new FormControl(),
      waypointInterval: new FormControl(),
      latitude: new FormControl(),
      longitude: new FormControl(),
      mainContactNumber: new FormControl(),
      emergencyContactNumber: new FormControl(),
      altEmergencyContactNumber: new FormControl(),
      addAssetCompetencies: new FormControl(false),
    });
  }

  ngOnInit() {
    this.getSiteOwners();
    this.getSiteTypes();
  }

  // Get Site Owners
  public getSiteOwners() {
    this.siteOwnerUserService.getAllEnabledSiteOwnersForDropdowns()
      .subscribe((data: any) => {
        this.siteOwners.push(this.selectedOwner);
        data.forEach(element => {
          const obj = {
            label: element.firstName + ' ' + element.lastName,
            value: element
          };
          this.siteOwners.push(obj);
        });
      });
  }

  // Get Site Types
  public getSiteTypes() {
    this.siteTypesService.getAllSiteTypesNoPagination()
      .subscribe((data: any) => {
        this.siteTypes.push(this.selectedSiteType);
        data.forEach(element => {
          const obj = {
            label: element.name,
            value: element
          };
          this.siteTypes.push(obj);
        })
      })
  }

  // Handle Site Owner onChange event
  public onSelectedSiteOwnerChange(event) {
    this.selectedOwner = event;
    this.getSiteInductionsForSelectedOwner(this.selectedOwner.value.id);
  }

  // Handle Site Type onChange event
  public onSelectedSiteTypeChange(event) {
    this.selectedSiteType = event;
  }

  // Handle getting site inductions on site owner select
  public getSiteInductionsForSelectedOwner(id) {
    this.siteInductions = []; // reset inductions array on change of owner
    this.trainingService.getTrainingCoursesBySiteOwnerId(id)
      .subscribe((data: any) => {
        this.siteInductions.push(this.selectedSiteInduction);
        data.forEach(element => {
          const obj = {
            label: element.name,
            value: element
          };
          this.siteInductions.push(obj);
        })
      })
  }

  // Handle Site Induction onChange event
  public onSelectedSiteInductionChange(event) {
    this.selectedSiteInduction = event;
  }

  // Handle Address Change
  public handleAddressChange(address: Address) {
    this.lat = address.geometry.location.lat();
    this.lng = address.geometry.location.lng();
    this.createSiteForm.patchValue({
      latitude: this.lat,
      longitude: this.lng
    });
  }

  // Handle Marker Drag Event
  public markerDragEnd(event) {
    this.lat = event.coords.lat;
    this.lng = event.coords.lng;
    this.createSiteForm.patchValue({
      latitude: this.lat,
      longitude: this.lng
    });
  }

  // Create Site
  public onCreateSiteFormSubmit() {
    const values = this.createSiteForm.value;
    const body = {
      name: values.name,
      description: values.description,
      latitude: values.latitude,
      longitude: values.longitude,
      healthCheckInterval: values.waypointInterval,
      siteTypeId: this.selectedSiteType.value.id,
      skylarkUserId: this.selectedOwner.value.id,
      addInduction: true,
      addAssetCompetencies: values.addAssetCompetencies,
      mainContactNumber: values.mainContactNumber,
      emergencyContactNumber: values.emergencyContactNumber,
      altEmergencyContactNumber: values.altEmergencyContactNumber
    };
    this.siteService.createSite(body)
      .subscribe((data: any) => {
        if (this.selectedSiteInduction.value !== null) {
          this.trainingService.addInductionToSite(data.id, this.selectedSiteInduction.value.id)
            .subscribe((inductionData: any) => {
              this.successReset();
            });
        } else {
          this.successReset();
        }
      })
  }

  public successReset() {
    this.siteOwners = [];
    this.selectedOwner = { label: 'Select Site Owner', value: null };
    this.siteTypes = [];
    this.selectedSiteType = { label: 'Select Site Type', value: null };
    this.siteInductions = [];
    this.selectedSiteInduction = { label: 'Select Site Induction', value: null };
    this.createSiteForm.reset();
    this.notificationService.notify(
      'success',
      'Site Create',
      'Site has successfully been created.',
      'top',
      'right'
    );
    this.router.navigate(['/' + this.routeRole + '/sites']);
  }

}

interface Marker {
  lat: number;
  lng: number;
  draggable?: boolean;
}
